import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { IntakeBriefingAction, IntakeServiceId } from '../types';

interface IntakeBriefingCardProps {
  actions: IntakeBriefingAction[];
  serviceLabels: Record<IntakeServiceId, string>;
  onAction?: (action: IntakeBriefingAction) => void;
}

const TONE_COLORS = {
  default: 'primary',
  warning: 'warning',
  error: 'error',
  success: 'success',
} as const;

export function IntakeBriefingCard({ actions, serviceLabels, onAction }: IntakeBriefingCardProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.25 },
        borderRadius: 2,
        bgcolor: theme.palette.primary.dark,
        background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
        boxShadow: `0 12px 30px ${alpha(theme.palette.primary.dark, 0.14)}`,
        height: '100%',
      }}
    >
      <Stack spacing={0.25}>
        <Box>
          <Typography
            variant="overline"
            sx={{ color: alpha(theme.palette.common.white, 0.62), fontWeight: 800 }}
          >
            Needs attention
          </Typography>
          <Typography variant="h6" sx={{ mt: 0.25, color: 'common.white' }}>
            {actions.length > 0
              ? `${actions.length} item${actions.length === 1 ? '' : 's'} blocking your services`
              : 'Nothing needs your attention right now'}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: alpha(theme.palette.common.white, 0.72) }}
          >
            {actions.length > 0
              ? 'These items still need information, documents, or review.'
              : 'New open items will appear here.'}
          </Typography>
        </Box>
      </Stack>

      {actions.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1.75 }}>
          {actions.map(action => {
            const color = TONE_COLORS[action.tone];
            const Icon = action.icon;
            return (
              <Box
                component="button"
                type="button"
                key={action.id}
                onClick={() => onAction?.(action)}
                sx={{
                  appearance: 'none',
                  border: 0,
                  display: 'grid',
                  gridTemplateColumns: { xs: 'auto 1fr', sm: 'auto 1fr auto' },
                  gap: 1,
                  alignItems: 'center',
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.common.white, 0.095),
                  cursor: onAction ? 'pointer' : 'default',
                  textAlign: 'left',
                  width: '100%',
                  transition: 'background-color 0.12s ease, transform 0.12s ease',
                  '&:hover': onAction
                    ? {
                        bgcolor: alpha(theme.palette.common.white, 0.15),
                      }
                    : undefined,
                  '&:focus-visible': {
                    outline: `2px solid ${alpha(theme.palette.common.white, 0.72)}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.25,
                    display: 'grid',
                    placeItems: 'center',
                    color:
                      color === 'warning'
                        ? theme.palette.warning.light
                        : alpha(theme.palette.common.white, 0.88),
                    bgcolor: alpha(theme.palette.common.white, 0.13),
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ color: 'common.white', fontWeight: 750 }}>
                      {action.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: alpha(theme.palette.common.white, 0.72), fontWeight: 800 }}
                    >
                      {serviceLabels[action.serviceId]}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{ color: alpha(theme.palette.common.white, 0.68) }}
                  >
                    {action.description}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    gridColumn: { xs: '2 / -1', sm: 'auto' },
                    justifySelf: { xs: 'flex-start', sm: 'end' },
                    px: 1,
                    py: 0.5,
                    borderRadius: 999,
                    color: theme.palette.primary.dark,
                    bgcolor: alpha(theme.palette.common.white, 0.9),
                    fontWeight: 850,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {action.actionLabel}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
